import type { Task, StartPeriod } from '@/src/renderer/lib/db/models'

/**
 * One discrete decision the user makes about a task — either inside the
 * Clarify-Mode (Kurz notiert) or via the dormant Reactivate dropdown.
 *
 * `wakeInProject` is Reactivate-only and unpauses the task without changing
 * its dates / project assignment — used when a paused task already has a
 * project and the user just wants to wake it up there again.
 */
export type StagedAction =
  | { kind: 'today' }
  | { kind: 'tomorrow' }
  | { kind: 'later'; date: Date; time: string | null; period: StartPeriod | null }
  | { kind: 'pause' }
  | { kind: 'move'; projectId: string; projectName: string }
  | { kind: 'delete' }
  /**
   * Reactivate-only: clears `pausedAt` without touching dates or
   * `projectId`. Carries `projectId` + `projectName` + `lifeSpace` so
   * the toast can offer an "open project" button.
   */
  | { kind: 'wakeInProject'; projectId: string; projectName: string; lifeSpace: 'personal' | 'work' | 'home' }

/**
 * Project a `StagedAction` into the `Partial<Task>` payload that gets
 * written to the DB. Single source of truth for both Clarify (batch
 * commit) and Reactivate (single-action commit).
 */
export function buildTaskUpdate(action: StagedAction, now: Date): Partial<Task> {
  switch (action.kind) {
    case 'today':
      return {
        startDate: atMidnight(now),
        startTime: null,
        startPeriod: null,
        pausedAt: null,
      }
    case 'tomorrow': {
      const tomorrow = new Date(now)
      tomorrow.setDate(tomorrow.getDate() + 1)
      return {
        startDate: atMidnight(tomorrow),
        startTime: null,
        startPeriod: null,
        pausedAt: null,
      }
    }
    case 'later':
      return {
        startDate: atMidnight(action.date),
        startTime: action.time,
        startPeriod: action.period,
        pausedAt: null,
      }
    case 'pause':
      return { pausedAt: now }
    case 'move':
      return {
        projectId: action.projectId,
        sectionId: null,
        pausedAt: null,
      }
    case 'delete':
      return { deletedAt: now }
    case 'wakeInProject':
      return { pausedAt: null }
  }
}

function atMidnight(date: Date): Date {
  const d = new Date(date)
  d.setHours(0, 0, 0, 0)
  return d
}
